import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Package, ChevronRight, ChevronLeft, Plus, Star, Heart, Share2, Scale, ShoppingCart, Minus, Info, Truck, ArrowRight, Plane, ShieldCheck, Download, FileText } from 'lucide-react';
import { db, OperationType, handleFirestoreError } from '../../firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { useInstruments } from '../../contexts/SiteContexts';
import { useCart, useLanguage, useCurrency, useSiteSettings, useWishlist, useCompare } from '../../contexts/SiteContexts';
import { StorefrontHero } from './StorefrontHero';
import { StorefrontCategories } from './StorefrontCategories';
import { ProductSkeleton } from '../ui/Skeleton';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { cn } from '../../lib/utils';
import { LOGO_ICON, LOGO_FULL } from '../../assets/logos';

import { LoadingScreen } from '../ui/LoadingScreen';
import { QuickViewModal } from './QuickViewModal';
import { ImageModal } from '../ui/ImageModal';
import { CartSidebar } from './CartSidebar';
import { CustomInstrumentModal } from './CustomInstrumentModal';
import { ProductCard } from './ProductCard';
import { CheckoutModal } from './CheckoutModal';
import { AISearch } from './AISearch';
import { AppInstallBanner } from './AppInstallBanner';
import { preloadProductBatch } from '../../utils/imageOptimizer';
import { generateCategoryCatalogPdf } from '../../utils/catalogPdfGenerator';
import { toast } from 'react-hot-toast';

export const Storefront = () => {
  const [displayCount, setDisplayCount] = useState(40);
  const { instruments, loading: instrumentsLoading } = useInstruments();
  const [customRequests, setCustomRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showCustomRequestModal, setShowCustomRequestModal] = useState(false);

  useEffect(() => {
    (window as any).showGlobalSellModal = () => setShowCustomRequestModal(true);
    return () => { delete (window as any).showGlobalSellModal; };
  }, []);
  const [selectedImg, setSelectedImg] = useState<{url: string, name: string} | null>(null);
  const { addToCart, isCheckoutOpen, setIsCheckoutOpen } = useCart();
  const { t } = useLanguage();
  const { formatPrice, formatAllPrices } = useCurrency();
  const { settings } = useSiteSettings();
  const location = useLocation();
  const navigate = useNavigate();

  // Optimized Search Debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400); // 400ms delay for performance
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setDisplayCount(40);
  }, [debouncedSearch, category]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const catParam = params.get('category');
    if (catParam) {
      // Redirect to the dedicated categories page instead of filtering home
      navigate(`/categories?category=${encodeURIComponent(catParam)}`, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    // Safety timeout to prevent infinite loading screen
    const safetyTimeout = setTimeout(() => {
      setLoading(false);
    }, 3500);

    const fetchRequests = async () => {
      setLoading(true);
      try {
        const qReq = query(collection(db, 'custom_requests'), where('status', '==', 'APPROVED'), where('type', '==', 'sell'), orderBy('createdAt', 'desc'), limit(100));
        const snapshot = await getDocs(qReq);
        setCustomRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), isMarketplaceItem: true })));
      } catch (error: any) {
        console.warn("Custom requests fetch error (likely quota limit exceeded):", error);
        try {
          handleFirestoreError(error, OperationType.LIST, 'custom_requests');
        } catch (err) {
          console.error("Non-fatal custom_requests error:", err);
        }
      } finally {
        setLoading(false);
        clearTimeout(safetyTimeout);
      }
    };

    fetchRequests();
    return () => clearTimeout(safetyTimeout);
  }, []);

  const allInstruments = useMemo(() => {
    const marketplaceItems = customRequests.map(req => ({
      ...req,
      name: req.productName || req.details || 'Medical Instrument',
      brand: req.doctorName || 'Doctor Seller',
      price: parseFloat(req.estimatedPrice) || 0,
      imageUrl: req.imageUrl || 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=800',
      category: req.category || 'Dr to Dr Hub',
      id: req.id
    }));
    return [...instruments, ...marketplaceItems];
  }, [instruments, customRequests]);

  const instrumentsByCategory = useMemo(() => {
    const groups: Record<string, any[]> = {};
    
    allInstruments.forEach(i => {
      const catInput = i.category?.trim();
      if (!catInput) return;
      
      let cat = catInput;
      if (cat.toLowerCase().includes('general')) cat = 'General Surgery';
      if (cat.toLowerCase().includes('electro')) cat = 'Electrosurgical';
      
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(i);
    });

    return groups;
  }, [allInstruments]);

  const allCategories = useMemo(() => {
    const defaultCats = settings.categories && settings.categories.length > 0 
      ? settings.categories 
      : ['Urology', 'Laparoscopic', 'Gynecology', 'Orthopedic', 'General Surgery', 'Electrosurgical'];
    
    const instrumentCats = Object.keys(instrumentsByCategory);
    const combined = Array.from(new Set([...defaultCats, ...instrumentCats]));
    return combined.filter(c => c && c.toLowerCase() !== 'all' && c !== 'Dr to Dr Hub');
  }, [settings.categories, instrumentsByCategory]);
  
  const filtered = useMemo(() => {
    const term = debouncedSearch.toLowerCase();
    return allInstruments.filter(i => {
      if (i.category === 'Dr to Dr Hub') return false;
      const matchesSearch = i.name.toLowerCase().includes(term) || 
                           i.description?.toLowerCase().includes(term) ||
                           (i.code && i.code.toLowerCase().includes(term));
      
      const iCatRaw = i.category?.trim().toLowerCase() || '';
      let iCat = iCatRaw;
      if (iCatRaw.includes('general')) iCat = 'general surgery';
      if (iCatRaw.includes('electro')) iCat = 'electrosurgical';
      
      const sCat = category.trim().toLowerCase();
      const matchesCategory = category === 'All' || iCat === sCat;
      
      return matchesSearch && matchesCategory;
    });
  }, [allInstruments, debouncedSearch, category]);

  // Background preloading of visible catalog photos for instant 0ms global delivery
  useEffect(() => {
    if (filtered.length > 0) {
      const topUrls = filtered.slice(0, 16).map(i => i.imageUrl).filter(Boolean);
      preloadProductBatch(topUrls, 16);
    }
  }, [filtered]);

  const storefrontLoadMoreRef = React.useRef<HTMLDivElement | null>(null);

  // Auto-paginate more products when scrolling into view without freezing UI
  useEffect(() => {
    const sentinel = storefrontLoadMoreRef.current;
    if (!sentinel) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first && first.isIntersecting) {
        setDisplayCount(prev => (prev < filtered.length ? prev + 32 : prev));
      }
    }, { rootMargin: '400px 0px' });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filtered.length, displayCount]);

  const getCategoryImage = (cat: string) => {
    const defaultImages: Record<string, string> = {
      'Urology': 'https://images.unsplash.com/photo-1579154234428-4582800f7cc7?auto=format&fit=crop&q=80&w=800',
      'Laparoscopic': 'https://images.unsplash.com/photo-1551076805-e1869033e561?auto=format&fit=crop&q=80&w=800',
      'Gynecology': 'https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&q=80&w=800',
      'Orthopedic': 'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?auto=format&fit=crop&q=80&w=800',
      'General Surgery': 'https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=800',
      'Electrosurgical': 'https://images.unsplash.com/photo-1581093458791-9f3c3250bb8b?auto=format&fit=crop&q=80&w=800',
      'Dr to Dr Hub': 'https://images.unsplash.com/photo-1559839734-2b71f1e59816?auto=format&fit=crop&q=80&w=800',
    };
    let nCat = cat;
    const lower = cat?.toLowerCase() || '';
    if (lower.includes('general')) nCat = 'General Surgery';
    if (lower.includes('electro')) nCat = 'Electrosurgical';
    
    return settings.categoryImages?.[cat] || defaultImages[nCat] || defaultImages[cat] || 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=75';
  };

  const handleDownloadCatalog = async (catName: string) => {
    const toastId = toast.loading(`Generating ${catName} PDF Catalog...`);
    try {
      await generateCategoryCatalogPdf({
        categoryName: catName,
        instruments,
        settings,
        pkrRate: settings.pkrRate || 280,
        cnyRate: settings.cnyRate || 7.2,
      });
      toast.success(`${catName} Catalog downloaded successfully!`, { id: toastId });
    } catch (err: any) {
      console.error('Catalog download failed:', err);
      toast.error(err.message || 'Failed to generate catalog PDF', { id: toastId });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 group/store">
      <StorefrontHero 
        settings={settings} 
        LOGO_FULL={LOGO_FULL} 
        search={search} 
        setSearch={setSearch} 
      />

      <AISearch />

      {category !== 'All' && (
        <div className="mb-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="p-3 bg-slate-100 text-slate-600 rounded-2xl hover:bg-slate-200 transition-all flex items-center gap-2 font-bold text-xs uppercase tracking-widest"
            >
              <ChevronRight className="rotate-180" size={16} />
              Back
            </button>
            <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight font-display">{category}</h2>
          </div>
          
          {category === 'Dr to Dr Hub' && (
            <button
              onClick={() => setShowCustomRequestModal(true)}
              className="flex items-center gap-3 px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-indigo-100 animate-pulse"
            >
              <Plus size={20} />
              Post Your Product
            </button>
          )}
        </div>
      )}

      {category === 'All' && !search ? (
        <div className="space-y-24">
          <StorefrontCategories 
            categories={allCategories} 
            getCategoryImage={getCategoryImage} 
          />

          {/* App Installation Banner */}
          <AppInstallBanner />

          {/* Quick Consignment Tracking Feature Banner */}
          <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 rounded-[2.5rem] p-8 md:p-12 text-white shadow-2xl relative overflow-hidden flex flex-col lg:flex-row items-center justify-between gap-8 border border-white/5">
            <div className="space-y-3 max-w-xl text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-widest text-indigo-300">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Live Logistics Partner Integration
              </div>
              <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white font-display">
                Real-Time Hospital Delivery & Air Waybill Tracking
              </h3>
              <p className="text-xs md:text-sm text-slate-300 font-medium">
                Monitor sterile seal verification, climate telemetry, and global airport checkpoint scans from our ISO 13485 cleanroom hub directly to your surgical department.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
              <button
                onClick={() => navigate('/tracking?id=HE-EXP-8942')}
                className="w-full sm:w-auto px-8 py-5 bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white font-black text-xs uppercase tracking-widest rounded-2xl transition-all shadow-xl shadow-indigo-600/30 flex items-center justify-center gap-2"
              >
                <Plane size={18} />
                Open Tracking Dashboard
                <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {['Urology', 'Laparoscopic', 'Gynecology', 'Orthopedic', 'General Surgery', 'Electrosurgical'].map(cat => {
            const catProducts = (instrumentsByCategory[cat] || []).slice(0, 10);
            if (catProducts.length === 0) return null;
            return (
              <div key={cat} className="space-y-8 overflow-hidden">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl md:text-3xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2 md:gap-3 font-display">
                    <div className="w-1.5 h-6 md:w-2 md:h-8 bg-indigo-600 rounded-full" />
                    <span className="truncate">{cat}</span>
                  </h3>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => {
                      const container = document.getElementById(`scroll-${cat.replace(/\s+/g, '-').toLowerCase()}`);
                      if (container) container.scrollBy({ left: -250, behavior: 'smooth' });
                    }}
                    className="p-2 bg-white border border-slate-100 rounded-full text-slate-400 hover:text-indigo-600 hover:border-indigo-100 transition-all shadow-sm"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button 
                    onClick={() => {
                      const container = document.getElementById(`scroll-${cat.replace(/\s+/g, '-').toLowerCase()}`);
                      if (container) container.scrollBy({ left: 250, behavior: 'smooth' });
                    }}
                    className="p-2 bg-white border border-slate-100 rounded-full text-slate-400 hover:text-indigo-600 hover:border-indigo-100 transition-all shadow-sm"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button 
                    onClick={() => handleDownloadCatalog(cat)}
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 text-slate-700 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all border border-slate-200 cursor-pointer shadow-xs"
                    title={`Download ${cat} PDF Catalog`}
                  >
                    <Download size={13} className="text-indigo-600" />
                    <span>Catalog PDF</span>
                  </button>
                  <button 
                    onClick={() => navigate(`/categories?category=${cat}`)}
                    className="text-xs font-black text-indigo-600 uppercase tracking-widest hover:translate-x-1 transition-transform flex items-center gap-1.5 ml-1"
                  >
                    View All <ChevronRight size={16} />
                  </button>
                </div>
              </div>
              <div className="relative group">
                  <div 
                    id={`scroll-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                    className="flex gap-4 md:gap-6 pb-6 pt-2 px-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
                  >
                    {catProducts.map((item, idx) => (
                      <div key={`${cat}-${item.id}`} className="snap-center shrink-0 w-[240px] sm:w-[260px] md:w-[280px] lg:w-[calc(25%-18px)]">
                        <ProductCard item={item} idx={idx} setSelectedItem={setSelectedItem} addToCart={addToCart} formatAllPrices={formatAllPrices} t={t} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => <ProductSkeleton key={i} />)
            ) : filtered.length > 0 ? (
              filtered.slice(0, displayCount).map((item, idx) => (
                <ProductCard key={item.id} item={item} idx={idx} setSelectedItem={setSelectedItem} addToCart={addToCart} formatAllPrices={formatAllPrices} t={t} />
              ))
            ) : (
              <div className="col-span-full text-center py-32 bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-200">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-slate-200">
                  <Package className="text-slate-200" size={40} />
                </div>
                <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight">{t('noInstruments')}</h3>
                <p className="text-slate-500 font-medium mt-2">Try adjusting your search or filters to find what you need.</p>
              </div>
            )}
          </div>
          {filtered.length > displayCount && (
            <div ref={storefrontLoadMoreRef} className="flex justify-center mt-12 pb-12">
              <button
                onClick={() => setDisplayCount(prev => prev + 32)}
                className="px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 hover:scale-105 transition-all shadow-xl shadow-indigo-200 cursor-pointer active:scale-95"
              >
                Load More Products ({Math.min(displayCount, filtered.length)} of {filtered.length})
              </button>
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {selectedItem && (
          <QuickViewModal
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
            onExpandImage={(url, name) => setSelectedImg({ url, name })}
          />
        )}
      </AnimatePresence>

      <ImageModal 
        url={selectedImg?.url || ''} 
        name={selectedImg?.name || ''} 
        isOpen={!!selectedImg} 
        onClose={() => setSelectedImg(null)} 
      />

      <CartSidebar instruments={instruments} />
      <CheckoutModal isOpen={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)} />
      <CustomInstrumentModal isOpen={showCustomRequestModal} onClose={() => setShowCustomRequestModal(false)} />
    </div>
  );
};

export default Storefront;